/**
 * 로컬 스냅샷 (계획서 §17-4). 암호화된 행을 그대로 복사하므로 키 없이도 만들 수 있고, 평문이 생기지 않는다.
 * migration·복원·대량 import 직전과 하루 1회 자동 생성, 최근 N개만 순환 보관.
 */
import { uuidv7 } from 'uuidv7';
import { isVaultHeader, type VaultHeader } from './crypto/vault';
import { META, type BlobChunk, type CounselDb, type SnapshotReason, type StoredRecord } from './db/schema';
import { isQuotaError, StorageFullError } from './errors';

export const SNAPSHOT_KEEP = 7;

export type SnapshotPayload = {
  vault: VaultHeader;
  dataSchemaVersion: number;
  entities: StoredRecord[];
  history: StoredRecord[];
  /** 원문 blob은 불변·삭제 불가라 로컬 스냅샷에는 넣지 않는다(용량). 백업 파일에는 포함된다. */
  blobs?: BlobChunk[];
};

export type SnapshotRecord = {
  id: string;
  createdAt: string;
  reason: SnapshotReason;
  schemaVersion: number;
  payload: SnapshotPayload;
};

export async function readAllRows(
  db: CounselDb,
  opts: { includeBlobs?: boolean } = {},
): Promise<SnapshotPayload | null> {
  return db.transaction('r', db.meta, db.entities, db.history, db.blobs, async () => {
    const vault = (await db.meta.get(META.vault))?.value;
    if (!isVaultHeader(vault)) return null;
    return {
      vault,
      dataSchemaVersion: ((await db.meta.get(META.dataSchemaVersion))?.value as number) ?? 1,
      entities: await db.entities.toArray(),
      history: await db.history.toArray(),
      ...(opts.includeBlobs === false ? {} : { blobs: await db.blobs.toArray() }),
    };
  });
}

export async function createSnapshot(db: CounselDb, reason: SnapshotReason, now = new Date()): Promise<string | null> {
  const payload = await readAllRows(db, { includeBlobs: false });
  if (!payload) return null;
  const id = uuidv7();
  try {
    await db.transaction('rw', db.snapshots, db.meta, async () => {
      await db.snapshots.put({
        id,
        createdAt: now.toISOString(),
        reason,
        schemaVersion: payload.dataSchemaVersion,
        payload,
      } satisfies SnapshotRecord);
      await db.meta.put({ key: META.lastSnapshotAt, value: now.toISOString() });
      const all = await db.snapshots.orderBy('createdAt').reverse().primaryKeys();
      for (const old of all.slice(SNAPSHOT_KEEP)) await db.snapshots.delete(old);
    });
  } catch (e) {
    if (isQuotaError(e)) throw new StorageFullError();
    throw e;
  }
  return id;
}

export async function listSnapshots(db: CounselDb): Promise<Omit<SnapshotRecord, 'payload'>[]> {
  const rows = (await db.snapshots.orderBy('createdAt').reverse().toArray()) as unknown as SnapshotRecord[];
  return rows.map(({ payload: _p, ...meta }) => meta);
}

/**
 * DB 내용을 payload로 통째로 교체 (단일 트랜잭션). 실패하면 아무것도 바뀌지 않는다.
 * deviceId·자동잠금 등 기기 설정은 유지한다.
 */
export async function replaceAllRows(db: CounselDb, p: SnapshotPayload): Promise<void> {
  try {
    await db.transaction('rw', [db.meta, db.entities, db.history, db.blobs, db.outbox], async () => {
      await Promise.all([db.entities.clear(), db.history.clear(), db.outbox.clear()]);
      // 원문 blob은 payload에 있을 때만 교체 (스냅샷에는 없음 → 기존 원문 유지)
      if (p.blobs) {
        await db.blobs.clear();
        await db.blobs.bulkPut(p.blobs);
      }
      await db.entities.bulkPut(p.entities);
      await db.history.bulkPut(p.history);
      const at = new Date().toISOString();
      await db.outbox.bulkAdd(
        p.entities.map((r) => ({ entityType: r.entityType, id: r.id, revision: r.revision, queuedAt: at })),
      );
      await db.meta.put({ key: META.vault, value: p.vault });
      await db.meta.put({ key: META.dataSchemaVersion, value: p.dataSchemaVersion });
    });
  } catch (e) {
    if (isQuotaError(e)) throw new StorageFullError();
    throw e;
  }
}

/** 스냅샷으로 되돌리기. 되돌리기 전 현재 상태도 스냅샷으로 남긴다. */
export async function restoreSnapshot(db: CounselDb, snapshotId: string): Promise<void> {
  const snap = (await db.snapshots.get(snapshotId)) as unknown as SnapshotRecord | undefined;
  if (!snap) throw new Error('스냅샷 없음');
  await createSnapshot(db, 'pre-restore');
  await replaceAllRows(db, snap.payload);
}
